<template>
    <Dialog :visible="visible" @update:visible="$emit('update:visible', $event)" modal header="Rediger bruker" style="width: 25rem">

        <form v-if="user" @submit.prevent="save">
            <div class="user-edit-dialog__field">
                <label for="user-name" class="user-edit-dialog__label">Navn</label>
                <InputText id="user-name" v-model="editForm.name" style="width: 100%" :invalid="!!errors.name" autofocus />
                <small v-if="errors.name" class="user-edit-dialog__error">{{ errors.name[0] }}</small>
            </div>

            <div class="user-edit-dialog__field">
                <label for="user-email" class="user-edit-dialog__label">E-post</label>
                <InputText id="user-email" v-model="editForm.email" type="email" style="width: 100%" :invalid="!!errors.email" />
                <small v-if="errors.email" class="user-edit-dialog__error">{{ errors.email[0] }}</small>
            </div>

            <div class="user-edit-dialog__field">
                <label for="user-location" class="user-edit-dialog__label">Kommune</label>
                <Select
                    id="user-location"
                    v-model="editForm.location"
                    :options="municipalities"
                    optionLabel="name"
                    optionValue="id"
                    filter
                    style="width: 100%"
                    :invalid="!!errors.location"
                />
                <small v-if="errors.location" class="user-edit-dialog__error">{{ errors.location[0] }}</small>
            </div>

            <div class="user-edit-dialog__field">
                <label for="user-mobile" class="user-edit-dialog__label">Mobil</label>
                <InputText id="user-mobile" v-model="editForm.mobile" style="width: 100%" :invalid="!!errors.mobile" />
                <small v-if="errors.mobile" class="user-edit-dialog__error">{{ errors.mobile[0] }}</small>
            </div>

            <div class="user-edit-dialog__field user-edit-dialog__field--switch">
                <ToggleSwitch id="user-anonymous" v-model="editForm.anonymous" />
                <label for="user-anonymous">Skjult fra toplister</label>
            </div>

            <div class="user-edit-dialog__field user-edit-dialog__field--switch">
                <ToggleSwitch id="user-privilege" v-model="editForm.privilege" />
                <label for="user-privilege">Admin</label>
            </div>

            <div class="user-edit-dialog__field">
                <label class="user-edit-dialog__label">Brukergrupper</label>

                <div class="user-edit-dialog__groups">
                    <Chip
                        v-for="group of editForm.groups"
                        :key="group.id"
                        :label="group.name"
                        removable
                        @remove="removeGroup(group)"
                    />
                    <span v-if="editForm.groups.length === 0" class="user-edit-dialog__groups-empty">
                        Ingen brukergrupper.
                    </span>
                </div>

                <AutoComplete
                    v-model="groupQuery"
                    :suggestions="groupSuggestions"
                    optionLabel="name"
                    placeholder="Søk etter brukergruppe"
                    style="width: 100%"
                    :inputStyle="{ width: '100%' }"
                    @complete="searchGroups"
                    @item-select="addGroup($event.value)"
                />
            </div>

            <div class="user-edit-dialog__dialog-footer">
                <Button type="button" label="Avbryt" severity="secondary" @click="$emit('update:visible', false)" />
                <Button type="submit" label="Lagre" :loading="saving" />
            </div>
        </form>

        <div v-if="user" class="user-edit-dialog__actions">
            <Button
                type="button"
                label="Se registreringer"
                icon="pi pi-list"
                severity="secondary"
                outlined
                @click="showRegistrationsDialog = true"
            />
            <Button
                type="button"
                label="Endre passord"
                icon="pi pi-lock"
                severity="secondary"
                outlined
                @click="showPasswordDialog = true"
            />
        </div>
    </Dialog>

    <UserRegistrationsDialog
        v-model:visible="showRegistrationsDialog"
        :user="user"
    />

    <UserPasswordDialog
        v-model:visible="showPasswordDialog"
        :user="user"
    />
</template>
<script>
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import ToggleSwitch from 'primevue/toggleswitch';
import Chip from 'primevue/chip';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import UserRegistrationsDialog from './UserRegistrationsDialog';
import UserPasswordDialog from './UserPasswordDialog';

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]').content;
}

export default {
    components: {
        Dialog,
        InputText,
        Select,
        ToggleSwitch,
        Chip,
        AutoComplete,
        Button,
        UserRegistrationsDialog,
        UserPasswordDialog,
    },
    props: {
        visible: { type: Boolean, required: true },
        user: { type: Object, default: null },
        municipalities: { type: Array, default: () => [] },
        allGroups: { type: Array, default: () => [] },
    },
    emits: ['update:visible', 'saved'],
    setup() {
        return {
            toast: useToast(),
        };
    },
    data() {
        return {
            editForm: {
                name: '',
                email: '',
                location: null,
                mobile: '',
                anonymous: false,
                privilege: false,
                groups: [],
            },
            groupQuery: '',
            groupSuggestions: [],
            errors: {},
            saving: false,
            showRegistrationsDialog: false,
            showPasswordDialog: false,
        };
    },
    watch: {
        visible(isVisible) {
            if (!isVisible || !this.user) {
                return;
            }

            this.editForm = {
                name: this.user.name,
                email: this.user.email,
                location: this.user.location,
                mobile: this.user.mobile ?? '',
                anonymous: this.user.is_anonymous,
                privilege: this.user.is_admin,
                groups: [...(this.user.groups ?? [])],
            };
            this.errors = {};
            this.groupQuery = '';
            this.groupSuggestions = [];
            this.showRegistrationsDialog = false;
            this.showPasswordDialog = false;
        },
    },
    methods: {
        searchGroups(event) {
            const term = event.query.trim().toLowerCase();
            const assignedIds = new Set(this.editForm.groups.map(g => g.id));

            this.groupSuggestions = this.allGroups
                .filter(group => !assignedIds.has(group.id))
                .filter(group => group.name.toLowerCase().includes(term));
        },
        async addGroup(group) {
            this.groupQuery = '';

            try {
                const response = await fetch(`/api/v1/user/${this.user.id}/groups`, {
                    method: 'POST',
                    headers: {
                        'X-CSRF-TOKEN': csrfToken(),
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ usergroup_id: group.id }),
                });

                if (!response.ok) {
                    throw new Error('request failed');
                }

                const user = await response.json();
                this.editForm.groups = user.groups;
                this.$emit('saved', user);
            } catch (error) {
                console.error(error);
                this.toast.add({ severity: 'error', summary: 'Feil', detail: 'Kunne ikke legge til brukergruppen.', life: 3000 });
            }
        },
        async removeGroup(group) {
            try {
                const response = await fetch(`/api/v1/user/${this.user.id}/groups/${group.id}`, {
                    method: 'DELETE',
                    headers: {
                        'X-CSRF-TOKEN': csrfToken(),
                        Accept: 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('request failed');
                }

                const user = await response.json();
                this.editForm.groups = user.groups;
                this.$emit('saved', user);
            } catch (error) {
                console.error(error);
                this.toast.add({ severity: 'error', summary: 'Feil', detail: 'Kunne ikke fjerne brukergruppen.', life: 3000 });
            }
        },
        async save() {
            this.errors = {};
            this.saving = true;

            try {
                const response = await fetch(`/api/v1/user/${this.user.id}`, {
                    method: 'PUT',
                    headers: {
                        'X-CSRF-TOKEN': csrfToken(),
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(this.editForm),
                });

                if (response.status === 422) {
                    const data = await response.json();
                    this.errors = data.errors ?? {};
                    return;
                }

                if (!response.ok) {
                    throw new Error('request failed');
                }

                const user = await response.json();
                this.$emit('saved', user);
                this.$emit('update:visible', false);
                this.toast.add({ severity: 'success', summary: 'Lagret', detail: 'Brukeren ble oppdatert.', life: 3000 });
            } catch (error) {
                console.error(error);
                this.toast.add({ severity: 'error', summary: 'Feil', detail: 'Kunne ikke lagre brukeren.', life: 3000 });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
<style scoped>
.user-edit-dialog__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 1.25rem;
}

.user-edit-dialog__field {
    margin-bottom: 1.25rem;
}

.user-edit-dialog__field--switch {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.user-edit-dialog__label {
    display: block;
    margin-bottom: 0.5rem;
}

.user-edit-dialog__error {
    color: var(--p-red-500, #e24c4c);
}

.user-edit-dialog__dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}

.user-edit-dialog__groups {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
}

.user-edit-dialog__groups-empty {
    color: var(--p-text-muted-color, #6c757d);
    font-size: 0.9rem;
}
</style>
